import { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import ChartCard, { Segmented, ShowAllChip } from "./ChartCard";
import SortedBarChart from "./SortedBarChart";
import { COLORS, MOTION_MS, SERIES, fmtCount, reducedMotion } from "./emsTheme";

const MAX_SLICES = 5; // five series colours, then "Others" in context grey

const shareText = (n, total) => {
    if (!total || !n) return "0%";
    const p = (n * 100) / total;
    return p < 1 ? "<1%" : `${Math.round(p)}%`;
};

/**
 * Donut with a clickable legend (point 4 of the feedback: breakdowns at the top, as a pie). Top
 * five slices in the series colours, the rest grouped as Others. Hovering a slice or legend row
 * shows it in the centre; clicking opens those tickets. A Donut / Bars switch shows the same data
 * as a sorted bar chart.
 * rows: [{ key, label, value }]
 */
const DonutCard = ({ eyebrow, title, info, rows, unitLabel = "tickets", onSelect, cardState, fileName, focused }) => {
    const [mode, setMode] = useState("donut");
    const [hover, setHover] = useState(null);
    const motion = !reducedMotion();
    // Only when the rows change: a new slices array on each hover restarted the Pie's animation.
    const { sorted, total, slices } = useMemo(() => {
        const sortedRows = [...(rows || [])].filter((r) => r.value > 0).sort((a, b) => b.value - a.value);
        const top = sortedRows.slice(0, MAX_SLICES);
        const rest = sortedRows.slice(MAX_SLICES);
        return {
            sorted: sortedRows,
            total: sortedRows.reduce((a, r) => a + r.value, 0),
            slices: rest.length
                ? [...top, { key: "__others", label: `Others (${rest.length})`, value: rest.reduce((a, r) => a + r.value, 0), others: true }]
                : top,
        };
    }, [rows]);
    const colorOf = (s, i) => (s.others ? COLORS.context : SERIES[i % SERIES.length]);
    const shown = hover != null ? slices[hover] : null;
    const pick = (s) => { if (onSelect && !s.others && s.key != null) onSelect(s); };

    return (
        <ChartCard {...cardState} eyebrow={eyebrow} title={title} info={info} empty={cardState.empty || (!cardState.loading && !cardState.error && !total)}
            fileName={fileName} height={180} ariaSummary={title}
            table={{ columns: [{ key: "label", label: eyebrow }, { key: "value", label: "Tickets", align: "right", text: (r) => fmtCount(r.value) }, { key: "share", label: "Share", align: "right", text: (r) => shareText(r.value, total) }], rows: sorted }}
            actions={(
                <>
                    {focused && <ShowAllChip label={focused.label} onClear={focused.onClear} />}
                    <Segmented label="Chart type" value={mode} onChange={setMode} options={[{ value: "donut", label: "Donut" }, { value: "bars", label: "Bars" }]} />
                </>
            )}>
            {mode === "bars" ? (
                <SortedBarChart rows={sorted.map((r) => ({ ...r, denominator: total }))} denominatorLabel={unitLabel} onSelect={onSelect} ariaLabel={title} />
            ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "152px minmax(0, 1fr)" }, gap: 2.5, alignItems: "center" }}>
                    <Box sx={{ position: "relative", height: 152, width: 152, mx: { xs: "auto", sm: 0 } }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={slices} dataKey="value" nameKey="label" innerRadius={50} outerRadius={72} paddingAngle={slices.length > 1 ? 2 : 0}
                                    startAngle={90} endAngle={-270} stroke="none" isAnimationActive={motion} animationDuration={MOTION_MS}
                                    onMouseEnter={(_, i) => setHover(i)} onMouseLeave={() => setHover(null)} onClick={(d) => pick(d.payload || d)}>
                                    {slices.map((s, i) => (
                                        <Cell key={s.key ?? s.label} fill={colorOf(s, i)} cursor={onSelect && !s.others ? "pointer" : "default"}
                                            opacity={hover == null || hover === i ? 1 : 0.35} style={{ transition: "opacity 150ms" }} />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                        <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none", textAlign: "center", px: 3 }}>
                            <Box>
                                <Typography sx={{ fontSize: 24, fontWeight: 600, color: COLORS.ink, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
                                    {fmtCount(shown ? shown.value : total)}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: COLORS.ink3, mt: 0.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 90 }}>
                                    {shown ? shown.label : unitLabel}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Box component="ul" aria-label={`${title}, legend`} sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 0.5 }}>
                        {slices.map((s, i) => {
                            const clickable = onSelect && !s.others && s.key != null;
                            return (
                                <Box component="li" key={s.key ?? s.label}>
                                    <Box component={clickable ? "button" : "div"} type={clickable ? "button" : undefined} onClick={clickable ? () => pick(s) : undefined}
                                        onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onFocus={() => setHover(i)} onBlur={() => setHover(null)}
                                        aria-label={`${s.label}: ${fmtCount(s.value)} ${unitLabel}, ${shareText(s.value, total)}${clickable ? ". Focus the page on this" : ""}`}
                                        sx={{ all: "unset", boxSizing: "border-box", width: "100%", display: "grid", gridTemplateColumns: "10px minmax(0, 1fr) auto 44px", alignItems: "center", gap: 1,
                                            px: 1, py: 0.6, borderRadius: "6px", cursor: clickable ? "pointer" : "default",
                                            bgcolor: hover === i ? COLORS.ground : "transparent", transition: "background-color 150ms",
                                            "&:focus-visible": { outline: `2px solid ${COLORS.purple}` } }}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: colorOf(s, i) }} />
                                        <Typography title={s.label} sx={{ fontSize: 13, color: COLORS.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.label}</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.ink, fontVariantNumeric: "tabular-nums" }}>{fmtCount(s.value)}</Typography>
                                        <Typography sx={{ fontSize: 12, color: COLORS.ink3, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{shareText(s.value, total)}</Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}
        </ChartCard>
    );
};

export default DonutCard;
